/**
 * PartnerApply — public page for YouTubers, CPAs, podcasters, and finance
 * influencers to apply to the Central Rentals Partner Program (30% recurring
 * commission on every referred customer).
 *
 * Routes:
 *   /partners/apply          — application form + leaderboard
 *   /partners/:code          — partner dashboard (token-gated via ?t=)
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Award, DollarSign, Users, TrendingUp, Sparkles, Loader2, Copy, Check, ExternalLink, Trophy } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }} data-testid="partner-copy-btn" className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] uppercase tracking-wide font-bold text-emerald-700 hover:bg-emerald-50">
      {done ? <Check className="w-3 h-3"/> : <Copy className="w-3 h-3"/>}{done ? "Copied" : "Copy"}
    </button>
  );
}

function StripeConnectCard({ partner, code, token }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function connect() {
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/api/partners/${code}/connect/onboarding`, {
        method: "POST", headers: { "X-Partner-Token": token },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      if (d.onboarding_url) window.location.href = d.onboarding_url;
    } catch (e) { setError(String(e.message || e)); setBusy(false); }
  }
  if (partner.stripe_payouts_enabled) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center justify-between" data-testid="stripe-connected-card">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Stripe Connect</div>
          <div className="text-sm font-bold text-emerald-900">✓ Connected — payouts will land in your bank automatically</div>
        </div>
        <span className="px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-bold">Active</span>
      </div>
    );
  }
  if (partner.stripe_connected) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="stripe-pending-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Stripe Connect</div>
            <div className="text-sm font-bold text-amber-900">Onboarding in progress — finish a few details to enable auto-payouts</div>
          </div>
          <button onClick={connect} disabled={busy} data-testid="stripe-resume-btn" className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin inline"/> : "Resume onboarding →"}
          </button>
        </div>
        {error && <div className="text-xs text-rose-600 mt-2">{error}</div>}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="stripe-connect-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Get paid automatically</div>
          <div className="font-bold text-slate-900">Connect Stripe to receive payouts straight to your bank</div>
          <div className="text-xs text-slate-500 mt-1">Without Stripe Connect, payouts settle manually via e-transfer.</div>
        </div>
        <button onClick={connect} disabled={busy} data-testid="stripe-connect-btn" className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Opening Stripe…</> : <><DollarSign className="w-4 h-4"/> Connect Stripe</>}
        </button>
      </div>
      {error && <div className="text-xs text-rose-600 mt-2">{error}</div>}
    </div>
  );
}

function Leaderboard() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/partners/leaderboard?limit=10`).then((r) => r.ok ? r.json() : null).then((d) => setItems(d?.items || []));
  }, []);
  if (items.length === 0) return null;
  return (
    <section className="mt-12 max-w-3xl mx-auto" data-testid="partner-leaderboard">
      <h2 className="text-2xl font-extrabold text-slate-900 mb-2 flex items-center gap-2"><Trophy className="w-6 h-6 text-amber-500"/> Top partners this month</h2>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest font-bold text-slate-500">
            <tr><th className="px-4 py-2 text-left">#</th><th className="px-4 py-2 text-left">Partner</th><th className="px-4 py-2 text-left">Channel</th><th className="px-4 py-2 text-right">Refs</th><th className="px-4 py-2 text-right">Earned</th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.rank} className="border-t border-slate-100">
                <td className="px-4 py-2 font-bold">#{p.rank}</td>
                <td className="px-4 py-2 font-mono text-xs">{p.name_masked}</td>
                <td className="px-4 py-2 text-slate-700">{p.channel}</td>
                <td className="px-4 py-2 text-right tabular-nums font-bold">{p.conversions}</td>
                <td className="px-4 py-2 text-right tabular-nums font-bold text-emerald-700">${p.earned_cad.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ApplyForm({ onApplied }) {
  const [form, setForm] = useState({ name: "", email: "", channel: "youtube", handle: "", audience_size: "", pitch: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const body = {
        ...form,
        audience_size: form.audience_size ? Number(form.audience_size) : null,
      };
      const r = await fetch(`${API}/api/partners/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      setResult(d);
      // Fire Google Ads + Meta conversion for the partner application
      // (these are high-intent leads — count them).
      try {
        const g = await import("@/lib/google-tag");
        g.fireLeadFormSubmit({
          source: "partner_apply", email: form.email.trim(), valueCad: 30,
          transactionId: `partner_${d?.code || ""}`,
        });
      } catch { /* never block UX */ }
      onApplied?.(d);
    } catch (e) { setError(String(e.message || e)); } finally { setBusy(false); }
  }

  if (result) {
    return (
      <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="partner-apply-success">
        <Sparkles className="w-10 h-10 text-emerald-600 mx-auto mb-3"/>
        <h2 className="text-2xl font-extrabold text-slate-900 mb-2">You're in 🎉</h2>
        <p className="text-slate-700 mb-4">Your partner code is ready. Share this link anywhere — every referred customer earns you <b>30% of their monthly subscription forever</b>.</p>
        <div className="inline-flex items-center gap-2 px-4 py-3 bg-white rounded-lg border border-slate-200 mb-4">
          <code className="text-sm font-mono font-bold text-slate-900" data-testid="partner-share-url">{result.share_url}</code>
          <CopyBtn text={result.share_url}/>
        </div>
        <div className="text-xs text-slate-500">Your code: <b className="font-mono">{result.code}</b> · Status: <b>{result.status}</b></div>
        {result.dashboard_url && (
          <a href={result.dashboard_url} className="inline-flex items-center gap-1 mt-4 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800" data-testid="partner-open-dashboard">
            <ExternalLink className="w-4 h-4"/> Open your dashboard
          </a>
        )}
        <p className="text-[11px] text-slate-500 mt-4">Bookmark your dashboard URL — it's how you'll track clicks, conversions, and pending payouts.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="partner-apply-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Your name *</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-name" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Email *</label>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="partner-email" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Channel *</label>
          <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="partner-channel" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white outline-none focus:border-emerald-500">
            <option value="youtube">YouTube</option>
            <option value="podcast">Podcast</option>
            <option value="blog">Blog / Newsletter</option>
            <option value="cpa">CPA / Accountant</option>
            <option value="realtor">Realtor</option>
            <option value="tiktok">TikTok</option>
            <option value="instagram">Instagram</option>
            <option value="linkedin">LinkedIn</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Handle / URL</label>
          <input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} data-testid="partner-handle" placeholder="@yourchannel or full URL" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Audience size (followers / subscribers / listeners)</label>
        <input type="number" min={0} value={form.audience_size} onChange={(e) => setForm({ ...form, audience_size: e.target.value })} data-testid="partner-audience" placeholder="e.g. 25000" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tell us about your audience (optional)</label>
        <textarea rows={3} value={form.pitch} onChange={(e) => setForm({ ...form, pitch: e.target.value })} data-testid="partner-pitch" placeholder="Are they Canadian landlords, real estate investors, CPAs, etc.?" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
      </div>
      {error && <div className="text-xs text-rose-600">{error}</div>}
      <button type="submit" disabled={busy} data-testid="partner-submit" className="w-full px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Generating your code…</> : <><Award className="w-4 h-4"/> Get my partner code</>}
      </button>
      <p className="text-[11px] text-slate-500 text-center">By applying, you agree to share your referral link in compliance with FTC / Canadian disclosure rules.</p>
    </form>
  );
}

export function PartnerApply() {
  useEffect(() => { document.title = "Partner Program — Central Rentals"; }, []);
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-emerald-50 to-amber-50" data-testid="partner-apply-page">
      <header className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="text-sm font-bold text-slate-900">← Central Rentals</Link>
        <Link to="/partners/apply#leaderboard" className="text-xs text-slate-600 hover:text-slate-900">Leaderboard</Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 pb-16">
        <section className="text-center pt-8 pb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold mb-4">
            <Award className="w-3.5 h-3.5"/> YOUTUBER + CPA PARTNER PROGRAM
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-4 leading-tight">
            Earn <span className="text-emerald-600">30% recurring</span><br/>on every customer you send us.
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            For Canadian YouTubers, CPAs, podcasters, and real-estate creators. Share your link → earn 30% of every subscription your audience starts, <b>every month they stay</b>. Paid via Stripe Connect.
          </p>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-12">
          <div className="rounded-xl bg-white border border-slate-200 p-5 text-center" data-testid="benefit-card-1">
            <DollarSign className="w-7 h-7 text-emerald-600 mx-auto mb-2"/>
            <div className="font-extrabold text-slate-900 mb-1">30% recurring</div>
            <div className="text-xs text-slate-500">Not a one-time bounty — earn every month they pay</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-5 text-center" data-testid="benefit-card-2">
            <Users className="w-7 h-7 text-emerald-600 mx-auto mb-2"/>
            <div className="font-extrabold text-slate-900 mb-1">Built for Canada</div>
            <div className="text-xs text-slate-500">13-province lease gen, T776 / NR4 exports, Stripe CAD</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-5 text-center" data-testid="benefit-card-3">
            <TrendingUp className="w-7 h-7 text-emerald-600 mx-auto mb-2"/>
            <div className="font-extrabold text-slate-900 mb-1">Live dashboard</div>
            <div className="text-xs text-slate-500">Track clicks, conversions, and pending payouts in real time</div>
          </div>
        </section>

        <section className="max-w-xl mx-auto bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <h2 className="text-2xl font-extrabold text-slate-900 mb-4 text-center">Apply in 60 seconds</h2>
          <ApplyForm/>
        </section>

        <section id="leaderboard"><Leaderboard/></section>
      </main>
    </div>
  );
}

export function PartnerDashboard() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = `Partner ${code} — Central Rentals`;
    async function load() {
      try {
        const r = await fetch(`${API}/api/partners/${code}/dashboard?t=${encodeURIComponent(token)}`);
        if (!r.ok) {
          const d = await r.json().catch(() => null);
          throw new Error(d?.detail || `HTTP ${r.status}`);
        }
        setData(await r.json());
      } catch (e) { setError(String(e.message || e)); }
    }
    load();
  }, [code, token]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 max-w-md text-center" data-testid="partner-dashboard-error">
          <div className="text-rose-800 font-bold mb-2">Can't load dashboard</div>
          <div className="text-xs text-rose-700">{error}</div>
          <Link to="/partners/apply" className="inline-block mt-4 text-sm text-emerald-700 underline">Back to partner program →</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400"/></div>;
  }

  const { partner, share_url, stats, recent_conversions, payouts } = data;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-dashboard">
      <header className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="font-display text-2xl font-extrabold">{partner.name}</div>
            <div className="text-xs text-slate-400 uppercase tracking-widest">{partner.channel} · code <b className="font-mono">{partner.code}</b> · {partner.status}</div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${partner.status === "active" ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"}`}>{partner.split_percent}% split</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="partner-share-section">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-1">Your share link</div>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-sm font-mono font-bold text-slate-900 bg-white px-3 py-2 rounded border border-emerald-200 flex-1">{share_url}</code>
            <CopyBtn text={share_url}/>
          </div>
        </div>

        <StripeConnectCard partner={partner} code={code} token={token}/>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-center" data-testid="stat-clicks"><div className="text-[10px] uppercase tracking-widest text-slate-500">Clicks</div><div className="text-2xl font-extrabold text-slate-900">{stats.clicks}</div></div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-center" data-testid="stat-conversions"><div className="text-[10px] uppercase tracking-widest text-slate-500">Conversions</div><div className="text-2xl font-extrabold text-slate-900">{stats.conversions_count}</div></div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center" data-testid="stat-pending"><div className="text-[10px] uppercase tracking-widest text-amber-700">Pending CAD</div><div className="text-2xl font-extrabold text-amber-800">${stats.pending_cad.toFixed(2)}</div></div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="stat-paid"><div className="text-[10px] uppercase tracking-widest text-emerald-700">Paid CAD</div><div className="text-2xl font-extrabold text-emerald-800">${stats.paid_cad.toFixed(2)}</div></div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 text-[10px] uppercase tracking-widest font-bold text-slate-500">Recent conversions</div>
          {recent_conversions.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No conversions yet. Share your link to start earning.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-widest font-bold text-slate-500"><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Kind</th><th className="px-4 py-2 text-left">Plan</th><th className="px-4 py-2 text-right">Your share</th><th className="px-4 py-2">Status</th></tr></thead>
              <tbody>
                {recent_conversions.map((c, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-xs text-slate-500">{new Date(c.ts).toLocaleDateString("en-CA")}</td>
                    <td className="px-4 py-2 text-slate-700">{c.kind}</td>
                    <td className="px-4 py-2 text-slate-700">{c.plan_id || "—"}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-bold">${(c.partner_share_cents/100).toFixed(2)}</td>
                    <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold ${c.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{c.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {payouts.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-2 bg-slate-50 text-[10px] uppercase tracking-widest font-bold text-slate-500">Payout history</div>
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-widest font-bold text-slate-500"><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-right">Convs</th><th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2">Status</th></tr></thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString("en-CA")}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{p.conversion_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-bold">${(p.total_cents/100).toFixed(2)}</td>
                    <td className="px-4 py-2"><span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold bg-slate-100 text-slate-600">{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

export default PartnerApply;
